/**
 * ServerSetupGate — second gate, runs after AuthGate.
 *
 * Until the active org has at least one media_server
 * linked, every panel in the app is empty (devices, agents, events all
 * come from servers). Rather than render an empty AppShell, redirect the
 * user to /setup-server and force them to run the pairing flow.
 *
 * The list is the servers atom's, shared with the app below, so the gate
 * costs no request of its own. It decides once per mount, from a load that
 * settles after it mounted, and holds that decision: a later reload never
 * unmounts the app or bounces it to setup. StoreProvider remounts it for
 * another organization, which is then decided afresh.
 *
 * It sits under AuthGate, so the user is signed in to an organization.
 */

import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useServersVerdict } from '@/hooks/useServersVerdict';
import { cn } from '@/lib/utils';
import { GateStatus } from '@/types/servers';

import { authSession } from './authSession';
import { getOrgChooserUrl } from './loginNext';

const RETRY_BUTTON_CLASS = cn(
    'rounded border border-occ-border-1 px-4 py-2 text-sm',
    'not-disabled:hover:bg-occ-foreground-25',
);
// The ways out beside Retry, drawn quieter than it.
const EXIT_BUTTON_CLASS = cn(RETRY_BUTTON_CLASS, 'text-occ-foreground-tertiary');

function CenteredSpinner(): ReactNode {
    return (
        <div className={cn(
            'theme-occurra flex h-dvh items-center justify-center bg-occ-background-main',
            'text-occ-foreground-tertiary',
        )}
        >
            <Loader2 className="size-6 animate-spin" />
        </div>
    );
}

interface Props {
    children: ReactNode;
}

export const ServerSetupGate = ({ children }: Props): ReactNode => {
    const { status, retry } = useServersVerdict();
    const loc = useLocation();
    const navigate = useNavigate();

    switch (status) {
        case GateStatus.Awaiting:
        case GateStatus.Pending:
        case GateStatus.Forbidden:
            return <CenteredSpinner />;
        case GateStatus.Error:
            return (
                <div className={cn(
                    'theme-occurra flex h-dvh flex-col items-center justify-center gap-3',
                    'bg-occ-background-main text-occ-foreground-main',
                )}
                >
                    <p className="text-sm text-occ-foreground-tertiary">
                        Could not reach the cloud service.
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            className={RETRY_BUTTON_CLASS}
                            onClick={retry}
                        >
                            Retry
                        </button>
                        {/* Never a dead end: a failure confined to this org must let the user
                            pick another — signing in again lands straight back in the
                            remembered one — and any failure must let them restart from a
                            clean login instead of clearing storage by hand. */}
                        <button
                            type="button"
                            className={EXIT_BUTTON_CLASS}
                            onClick={() => navigate(getOrgChooserUrl(loc.pathname + loc.search))}
                        >
                            Switch organization
                        </button>
                        <button
                            type="button"
                            className={EXIT_BUTTON_CLASS}
                            onClick={(): void => authSession.logout()}
                        >
                            Sign out
                        </button>
                    </div>
                </div>
            );
        case GateStatus.None:
            return (
                <Navigate
                    to={`/setup-server?next=${encodeURIComponent(loc.pathname + loc.search)}`}
                    replace
                />
            );
        case GateStatus.Has:
            return children;
        default:
            break;
    }
};
